/**
 * Replaces the chat on settings pages with a full-height picture of how settings
 * flow down: operator → client → debt type, and which level the page is editing.
 */

import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Skeleton } from '@ophelos/ui/components/skeleton';
import { editedLevel } from '@/apps/settings/lib/config-hierarchy';
import { useHubPathname } from '@/lib/global/hub-path';
import { useConfigHierarchy } from '../../hooks/useConfigHierarchy';
import { HierarchyLevelNode } from './_partials/HierarchyLevelNode';

export function SettingsHierarchyContent() {
  const pathname = useHubPathname();
  const { data, isPending, isError } = useConfigHierarchy(pathname);

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Could not load the settings for this context.</AlertDescription>
      </Alert>
    );
  }

  if (isPending || !data) {
    return (
      <div className="flex-1 stack gap-6 py-4">
        <Skeleton className="flex-1 w-full" />
        <Skeleton className="flex-1 w-full" />
        <Skeleton className="flex-1 w-full" />
      </div>
    );
  }

  const editing = editedLevel(data);

  return (
    <div className="flex-1 min-h-0 flex flex-col py-2">
      <div className="p-2 bg-muted rounded-lg">
        <p className="shrink-0 text-xs text-muted-foreground">
          Settings flow downwards. If no setting is set at a level, it inherits from the level above.
        </p>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {data.map((level, idx) => (
          <HierarchyLevelNode key={level.scope} level={level} isEditing={level.scope === editing} isLast={idx === data.length - 1} />
        ))}
      </div>
    </div>
  );
}
